import { useState } from "react";
import { toast } from "sonner";
import { Field, Card, SaveButton, GhostButton, FileLabel, inputCls, areaCls } from "../ui";
import { adminUpload, adminFetch } from "../adminApi";
import { VEHICLES } from "@/lib/site";

export default function PackagesTab({ content, reload }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(content.packages || [])));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);

  const update = (idx, key, value) =>
    setDraft((d) => d.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));

  const updateSub = (idx, cat, key, value) =>
    setDraft((d) =>
      d.map((p, i) => {
        if (i !== idx) return p;
        const obj = { ...(p[key] || {}) };
        obj[cat] = value;
        return { ...p, [key]: obj };
      })
    );

  const setFeatured = (idx) => setDraft((d) => d.map((p, i) => ({ ...p, featured: i === idx })));

  const toggleOptional = (idx, addonId) =>
    setDraft((d) =>
      d.map((p, i) => {
        if (i !== idx) return p;
        const list = p.optional_addon_ids || [];
        return { ...p, optional_addon_ids: list.includes(addonId) ? list.filter((x) => x !== addonId) : [...list, addonId] };
      })
    );

  const uploadImage = async (idx, file) => {
    setUploadingFor(idx);
    try {
      const r = await adminUpload(file);
      update(idx, "image_url", r.url);
      toast.success("Bild hochgeladen – jetzt unten auf Speichern klicken.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploadingFor(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminFetch("/admin/content/packages", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await reload();
      toast.success("Pakete & Preise gespeichert – live auf der Website.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-zinc-400 leading-relaxed">
        Namen, Beschreibungen, enthaltene Leistungen (eine pro Zeile), optionale Extras und Preise
        deiner Pakete. Preise &amp; Zeiten gelten in dieser Reihenfolge: Kleinwagen, Limousine/Kombi,
        SUV/Van, Transporter. Das "Hauptpaket" erscheint groß in der Leistungsübersicht.
      </p>

      {draft.map((p, idx) => (
        <Card
          key={p.id}
          title={p.name || `Paket ${idx + 1}`}
          actions={
            <GhostButton onClick={() => setFeatured(idx)} testid={`admin-package-featured-${p.id}`}>
              {p.featured ? "Hauptpaket" : "Als Hauptpaket"}
            </GhostButton>
          }
        >
          <Field label="Name">
            <input
              data-testid={`admin-package-name-${p.id}`}
              className={inputCls}
              value={p.name || ""}
              onChange={(e) => update(idx, "name", e.target.value)}
              maxLength={80}
            />
          </Field>
          <Field label="Kurzbeschreibung">
            <textarea
              data-testid={`admin-package-desc-${p.id}`}
              className={areaCls}
              value={p.desc || ""}
              onChange={(e) => update(idx, "desc", e.target.value)}
              maxLength={600}
            />
          </Field>
          <Field label="Enthaltene Leistungen (eine pro Zeile)">
            <textarea
              className={areaCls}
              value={(p.includes || []).join("\n")}
              onChange={(e) => update(idx, "includes", e.target.value.split("\n").filter((x) => x.trim() !== ""))}
            />
          </Field>

          <div>
            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400">
              Optionale Zusatzleistungen für dieses Paket (Häkchen = wird als „optional hinzubuchbar" gezeigt)
            </p>
            <div className="flex flex-wrap gap-2">
              {(content.addons || []).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  data-testid={`admin-package-optional-${p.id}-${a.id}`}
                  onClick={() => toggleOptional(idx, a.id)}
                  className={`rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${
                    (p.optional_addon_ids || []).includes(a.id)
                      ? "border-blue-500 bg-blue-500/10 text-blue-400"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  {a.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400">Grundpreis je Fahrzeugklasse (€, Ab-Preis)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {VEHICLES.map((v) => (
                <Field key={v.id} label={v.label}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={inputCls}
                    value={p.prices?.[v.id] ?? 0}
                    onChange={(e) => updateSub(idx, v.id, "prices", Number(e.target.value))}
                  />
                </Field>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400">Ungefähre Dauer je Klasse (Minuten)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {VEHICLES.map((v) => (
                <Field key={v.id} label={v.label}>
                  <input
                    type="number"
                    min="0"
                    className={inputCls}
                    value={p.durations?.[v.id] ?? 0}
                    onChange={(e) => updateSub(idx, v.id, "durations", Number(e.target.value))}
                  />
                </Field>
              ))}
            </div>
          </div>

          <Field label="Mögliche Zuschläge (eine pro Zeile, optional)">
            <textarea
              className={areaCls}
              value={(p.surcharges || []).join("\n")}
              onChange={(e) => update(idx, "surcharges", e.target.value.split("\n").filter((x) => x.trim() !== ""))}
              placeholder={"z. B. Tierhaare: nach Aufwand\nz. B. starke Verschmutzung: nach Absprache"}
            />
          </Field>

          <Field label="Hinweistext (wird unter dem Preis gezeigt)">
            <input
              className={inputCls}
              value={p.note || ""}
              onChange={(e) => update(idx, "note", e.target.value)}
              maxLength={400}
              placeholder="z. B. Alle Preise als Ab-Preise – Endpreis je nach Zustand"
            />
          </Field>

          <div>
            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400">Kartenbild</p>
            {p.image_url ? (
              <img src={p.image_url} alt={p.name} className="h-32 w-full max-w-xs rounded-xl object-cover border border-zinc-800" />
            ) : (
              <p className="text-xs text-zinc-400">Kein Bild gesetzt.</p>
            )}
            <div className="mt-2">
              <FileLabel testid={`admin-package-image-${p.id}`} onFile={(f) => uploadImage(idx, f)}>
                {uploadingFor === idx ? "Wird hochgeladen …" : "Bild hochladen / ersetzen"}
              </FileLabel>
            </div>
          </div>
        </Card>
      ))}

      <div className="sticky bottom-4 flex items-center gap-3 rounded-2xl border border-[#27272A] bg-[#0C0C0E]/95 p-4 backdrop-blur">
        <SaveButton onClick={save} saving={saving} saved={saved} testid="admin-save-packages" />
        <GhostButton onClick={() => setDraft(JSON.parse(JSON.stringify(content.packages || [])))} testid="admin-reset-packages">
          Änderungen verwerfen
        </GhostButton>
        <span className="ml-auto text-xs text-zinc-400 hidden sm:block">Änderungen erscheinen sofort auf der Website</span>
      </div>
    </div>
  );
}
